// --- STACKING.DAY ---
// Mempool.space-inspired cypherpunk aesthetic. Black, monospaced, orange accent.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell, Area, ComposedChart,
} from 'recharts';
import {
  Bitcoin, Calendar, DollarSign, Euro, TrendingUp, Database,
  Repeat, PoundSterling, Terminal, ChevronDown, ChevronUp,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// CONSTANTS
// ---------------------------------------------------------------------------
const TIME_RANGES = {
  '1M': { days: 30, label: '1 MONTH' },
  '3M': { days: 90, label: '3 MONTHS' },
  '6M': { days: 180, label: '6 MONTHS' },
  '1Y': { days: 365, label: '1 YEAR' },
  '2Y': { days: 730, label: '2 YEARS' },
  '4Y': { days: 1460, label: '4 YEARS' },
  CUSTOM: { days: 0, label: 'CUSTOM RANGE' },
};

const CURRENCIES = {
  usd: { symbol: '$', name: 'USD', icon: DollarSign },
  eur: { symbol: '€', name: 'EUR', icon: Euro },
  gbp: { symbol: '£', name: 'GBP', icon: PoundSterling },
  cad: { symbol: 'C$', name: 'CAD', icon: DollarSign },
  aud: { symbol: 'A$', name: 'AUD', icon: DollarSign },
  jpy: { symbol: '¥', name: 'JPY', icon: DollarSign },
};

const APIS = {
  coingecko: { label: 'COINGECKO', icon: Database },
  cryptocompare: { label: 'CRYPTOCOMPARE', icon: Database },
  alphavantage: { label: 'ALPHA VANTAGE', icon: Database },
  binance: { label: 'BINANCE', icon: Database },
  kraken: { label: 'KRAKEN', icon: Database },
};

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const Y_AXIS_DAY_LABELS_ORDERED = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const USE_LIVE_API = true;

// --- API KEYS (free tier, low risk) ---
const COINAPI_KEY = '30e3c09f-1af1-44c9-a65a-182abc93ff4f';
const ALPHAVANTAGE_KEY = 'O1XKD5N1N33SG6B4';

// ---------------------------------------------------------------------------
// THEME TOKENS — mempool-inspired
// ---------------------------------------------------------------------------
const THEME = {
  bg: '#11131a',
  bgPanel: '#181a24',
  bgPanelRaised: '#1f222e',
  border: '#2a2e3d',
  borderBright: '#3a3f52',
  text: '#e5e7eb',
  textDim: '#8b8fa3',
  textFaint: '#5b5f73',
  orange: '#f7931a',
  orangeDim: '#b86d10',
  green: '#3bb273',
  red: '#e5484d',
  indigo: '#6366f1',
  yellow: '#facc15',
  grid: '#24283a',
};

const SATOSHIS_PER_BTC = 100_000_000;

// ---------------------------------------------------------------------------
// DATA HELPERS
// ---------------------------------------------------------------------------
const fetchWithWeeklyCache = async (key, fetchFn) => {
  try {
    const cache = localStorage.getItem(key);
    if (cache) {
      const { timestamp, data } = JSON.parse(cache);
      if (Date.now() - timestamp < 7 * 24 * 60 * 60 * 1000) return data;
    }
  } catch (e) {
    console.error('Cache read error:', e);
  }
  const data = await fetchFn();
  try {
    localStorage.setItem(key, JSON.stringify({ timestamp: Date.now(), data }));
  } catch (e) {
    console.error('Cache write error:', e);
    if (e.name === 'QuotaExceededError') {
      console.log('Storage quota exceeded, clearing old cache...');
      localStorage.clear();
    }
  }
  return data;
};

const fetchCoinGeckoData = async (currency, fromTimestamp, toTimestamp) => {
  const from = Math.floor(fromTimestamp / 1000);
  const to = Math.floor(toTimestamp / 1000);
  const endpoint = `coins/bitcoin/market_chart/range?vs_currency=${currency}&from=${from}&to=${to}`;
  const url = `https://api.coingecko.com/api/v3/${endpoint}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`CoinGecko API error: ${res.status} ${res.statusText}`);
  return res.json();
};

const fetchCryptoCompareData = async (currency, fromTimestamp, toTimestamp) => {
  const symbol = 'BTC';
  let allPrices = [];
  let currentToTs = Math.floor(toTimestamp / 1000);
  const limitPerCall = 2000;
  const totalDays = Math.ceil((toTimestamp - fromTimestamp) / (1000 * 60 * 60 * 24));

  let daysFetched = 0;
  while (daysFetched < totalDays) {
    const limit = Math.min(limitPerCall, totalDays - daysFetched);
    const url = `https://min-api.cryptocompare.com/data/v2/histoday?fsym=${symbol}&tsym=${currency.toUpperCase()}&limit=${limit}&toTs=${currentToTs}`;
    const res = await fetch(url);
    const json = await res.json();
    if (!json.Data || !json.Data.Data || json.Response === 'Error') {
      throw new Error(`CryptoCompare API error: ${json.Message || 'Unknown error'}`);
    }
    const newPrices = json.Data.Data.map((x) => [x.time * 1000, x.close]);
    allPrices = newPrices.concat(allPrices);
    daysFetched += newPrices.length;
    if (newPrices.length > 0) {
      currentToTs = newPrices[0][0] / 1000 - 1;
    } else {
      break;
    }
  }
  const filteredPrices = allPrices.filter(([ts]) => ts >= fromTimestamp && ts <= toTimestamp);
  return { prices: filteredPrices.sort((a, b) => a[0] - b[0]) };
};

const fetchCoinApiData = async (currency, fromTimestamp, toTimestamp) => {
  if (COINAPI_KEY === 'YOUR_COINAPI_KEY' || !COINAPI_KEY) {
    throw new Error("CoinAPI.io requires an API key.");
  }
  const start = new Date(fromTimestamp);
  const end = new Date(toTimestamp);
  const periodId = '1DAY';
  const daysNeeded = Math.ceil((toTimestamp - fromTimestamp) / (1000 * 60 * 60 * 24));
  const url = `https://rest.coinapi.io/v1/ohlcv/BTC/${currency.toUpperCase()}/history?period_id=${periodId}&time_start=${start.toISOString()}&time_end=${end.toISOString()}&limit=${daysNeeded}`;

  const res = await fetch(url, { headers: { 'X-CoinAPI-Key': COINAPI_KEY } });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`CoinAPI.io API error: ${res.status} ${res.statusText} - ${errorText}`);
  }
  const json = await res.json();
  if (!json || json.length === 0) throw new Error('No price data returned from CoinAPI.io.');
  return { prices: json.map((d) => [new Date(d.time_period_start).getTime(), d.price_close]) };
};

const fetchAlphaVantageData = async (currency, fromTimestamp, toTimestamp) => {
  if (ALPHAVANTAGE_KEY === 'YOUR_ALPHAVANTAGE_KEY' || !ALPHAVANTAGE_KEY) {
    throw new Error("Alpha Vantage requires a personal API key from alphavantage.co.");
  }
  const url = `https://www.alphavantage.co/query?function=DIGITAL_CURRENCY_DAILY&symbol=BTC&market=${currency.toUpperCase()}&apikey=${ALPHAVANTAGE_KEY}`;
  const res = await fetch(url);
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Alpha Vantage API error: ${res.status} ${res.statusText} - ${errorText}`);
  }
  const json = await res.json();
  if (json['Error Message']) throw new Error(`Alpha Vantage API error: ${json['Error Message']}`);
  if (json['Note']) throw new Error(`Alpha Vantage API note: ${json['Note']}`);
  if (!json['Time Series (Digital Currency Daily)']) throw new Error('No price data returned from Alpha Vantage.');

  const prices = [];
  const timeSeries = json['Time Series (Digital Currency Daily)'];
  for (const dateStr in timeSeries) {
    const ts = new Date(dateStr).getTime();
    if (ts >= fromTimestamp && ts <= toTimestamp) {
      const price = parseFloat(timeSeries[dateStr][`4. close`]);
      if (!isNaN(price)) prices.push([ts, price]);
    }
  }
  prices.sort((a, b) => a[0] - b[0]);
  return { prices };
};

const fetchBinanceData = async (currency, fromTimestamp, toTimestamp, setWarning) => {
  let symbol = `BTC${currency.toUpperCase()}`;
  const interval = '1d';
  const maxLimit = 1000;
  let allPrices = [];
  let currentEndTime = toTimestamp;

  while (currentEndTime >= fromTimestamp) {
    let url = `https://api.binance.com/api/v3/klines?symbol=${symbol}&interval=${interval}&endTime=${currentEndTime}&limit=${maxLimit}`;
    let res;
    try {
      res = await fetch(url);
      if (!res.ok) {
        const errorText = await res.text();
        if (currency.toLowerCase() !== 'usd' && currency.toLowerCase() !== 'usdt') {
          setWarning(`No direct ${currency.toUpperCase()} pair on Binance. Fetching BTCUSDT and converting.`);
          symbol = `BTCUSDT`;
          url = `https://api.binance.com/api/v3/klines?symbol=${symbol}&interval=${interval}&endTime=${currentEndTime}&limit=${maxLimit}`;
          res = await fetch(url);
          if (!res.ok) throw new Error(`Binance API returned non-OK status for BTCUSDT: ${res.status} ${res.statusText} - ${errorText}`);
        } else {
          throw new Error(`Binance API returned non-OK status for ${symbol}: ${res.status} ${res.statusText} - ${errorText}`);
        }
      }
    } catch (e) {
      if (e instanceof TypeError && e.message === 'Failed to fetch') {
        throw new Error(`Network or CORS error when fetching ${symbol} from Binance. Try another API source.`);
      }
      throw e;
    }

    const json = await res.json();
    if (!json || json.length === 0) break;
    const newPrices = json.map((d) => [d[0], parseFloat(d[4])]);
    allPrices = newPrices.concat(allPrices);
    currentEndTime = newPrices[0][0] - 1;
  }

  const filteredPrices = allPrices.filter(([ts]) => ts >= fromTimestamp && ts <= toTimestamp);
  return { prices: filteredPrices.sort((a, b) => a[0] - b[0]) };
};

const fetchKrakenData = async (currency, fromTimestamp, toTimestamp, setWarning) => {
  let pair = `XBT${currency.toUpperCase()}`;
  const interval = 1440;
  let allPrices = [];
  let currentSince = Math.floor(fromTimestamp / 1000);

  while (true) {
    let url = `https://api.kraken.com/0/public/OHLC?pair=${pair}&interval=${interval}&since=${currentSince}`;
    let res;
    try {
      res = await fetch(url);
      if (!res.ok) {
        const errorText = await res.text();
        if (currency.toLowerCase() !== 'usd' && currency.toLowerCase() !== 'usdt') {
          setWarning(`No direct ${currency.toUpperCase()} pair on Kraken. Fetching XBTUSD and converting.`);
          pair = `XBTUSD`;
          url = `https://api.kraken.com/0/public/OHLC?pair=${pair}&interval=${interval}&since=${currentSince}`;
          res = await fetch(url);
          if (!res.ok) throw new Error(`Kraken API returned non-OK status for XBTUSD: ${res.status} ${res.statusText} - ${errorText}`);
        } else {
          throw new Error(`Kraken API returned non-OK status for ${pair}: ${res.status} ${res.statusText} - ${errorText}`);
        }
      }
    } catch (e) {
      if (e instanceof TypeError && e.message === 'Failed to fetch') {
        throw new Error(`Network or CORS error when fetching ${pair} from Kraken. Try another API source.`);
      }
      throw e;
    }

    const json = await res.json();
    if (json.error && json.error.length > 0) throw new Error(`Kraken API error: ${json.error.join(', ')}`);
    const dataKey = Object.keys(json.result).find((key) => key !== 'last');
    const ohlcData = json.result[dataKey];
    if (!ohlcData || ohlcData.length === 0) break;
    const newPrices = ohlcData.map((d) => [d[0] * 1000, parseFloat(d[4])]);
    allPrices = allPrices.concat(newPrices);
    currentSince = (newPrices[newPrices.length - 1][0] / 1000) + 1;
    if (newPrices[newPrices.length - 1][0] >= toTimestamp) break;
  }

  const filteredPrices = allPrices.filter(([ts]) => ts >= fromTimestamp && ts <= toTimestamp);
  return { prices: filteredPrices.sort((a, b) => a[0] - b[0]) };
};

const generateMockPriceData = (days, price, growth, vol) => {
  const arr = [];
  const now = Date.now();
  const dailyGrowth = Math.pow(growth, 1 / 365) - 1;
  let p = price / Math.pow(1 + dailyGrowth, days);
  const hours = days * 24;
  const hourlyGrowth = Math.pow(growth, 1 / (365 * 24)) - 1;
  for (let i = hours; i > 0; i--) {
    const ts = now - i * 3600000;
    p *= 1 + hourlyGrowth;
    const v = p * (1 + (Math.random() - 0.5) * vol);
    arr.push([ts, v > 0 ? v : p]);
  }
  return Promise.resolve({ prices: arr });
};

function fmtTime(ts) {
  return ts ? new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
}

const fetchAssetDataWithRange = async (apiSource, currency, days, startTs, endTs, setWarning) => {
  if (!USE_LIVE_API) return generateMockPriceData(days || 365, 68000, 2, 0.015);

  let fromTimestamp, toTimestamp, actualDays;
  if (startTs && endTs) {
    fromTimestamp = startTs;
    toTimestamp = endTs;
    actualDays = Math.ceil((endTs - startTs) / (1000 * 60 * 60 * 24));
  } else if (days) {
    toTimestamp = Date.now();
    fromTimestamp = toTimestamp - days * 86400000;
    actualDays = days;
  } else {
    throw new Error("Either 'days' or 'startTs' and 'endTs' must be provided.");
  }

  const cacheKey = `btc-${apiSource}-${currency}-${actualDays}-${fromTimestamp}-${toTimestamp}`;
  const fetchers = {
    coingecko: () => fetchCoinGeckoData(currency, fromTimestamp, toTimestamp),
    cryptocompare: () => fetchCryptoCompareData(currency, fromTimestamp, toTimestamp),
    coinapi: () => fetchCoinApiData(currency, fromTimestamp, toTimestamp),
    alphavantage: () => fetchAlphaVantageData(currency, fromTimestamp, toTimestamp),
    binance: () => fetchBinanceData(currency, fromTimestamp, toTimestamp, setWarning),
    kraken: () => fetchKrakenData(currency, fromTimestamp, toTimestamp, setWarning),
  };

  const fetcher = fetchers[apiSource];
  if (!fetcher) throw new Error('Invalid API source specified.');
  return fetchWithWeeklyCache(cacheKey, fetcher);
};

// ---------------------------------------------------------------------------
// ANALYTICS
// ---------------------------------------------------------------------------
function processPriceData(prices) {
  if (!prices || prices.length < 7) return [];

  const dailyStats = Array(7).fill().map(() => ({ totalPercentDiff: 0, count: 0, totalAbsolutePrice: 0 }));
  const dailyMinMax = Array(7).fill().map(() => ({ min: { p: Infinity, t: null }, max: { p: -Infinity, t: null } }));

  const movingAverages = prices.map((_, index) => {
    const windowPrices = [];
    for (let i = -3; i <= 3; i++) {
      const targetIndex = index + i;
      if (targetIndex >= 0 && targetIndex < prices.length) windowPrices.push(prices[targetIndex][1]);
    }
    if (windowPrices.length === 0) return 0;
    return windowPrices.reduce((sum, p) => sum + p, 0) / windowPrices.length;
  });

  prices.forEach(([ts, price], index) => {
    if (price <= 0) return;
    const dow = (new Date(ts).getUTCDay() + 6) % 7;
    const movingAverage = movingAverages[index];
    if (movingAverage > 0) {
      const percentDiff = ((price - movingAverage) / movingAverage) * 100;
      dailyStats[dow].totalPercentDiff += percentDiff;
      dailyStats[dow].count++;
      dailyStats[dow].totalAbsolutePrice += price;
    }
    const d = dailyMinMax[dow];
    if (price < d.min.p) d.min = { p: price, t: ts };
    if (price > d.max.p) d.max = { p: price, t: ts };
  });

  return DAY_NAMES.map((name, i) => {
    const stats = dailyStats[i];
    const minMax = dailyMinMax[i];
    const averagePercentDifference = stats.count > 0 ? stats.totalPercentDiff / stats.count : 0;
    const averagePriceForTooltip = stats.count > 0 ? stats.totalAbsolutePrice / stats.count : 0;
    return {
      name,
      percentDiff: averagePercentDifference,
      averagePrice: averagePriceForTooltip,
      minTime: minMax.min.t,
      maxTime: minMax.max.t,
    };
  });
}

function processTrendData(prices, windowDays = 90, stepDays = 7) {
  if (!prices || prices.length < windowDays) return [];
  const trendPoints = [];
  const windowMillis = windowDays * 86400000;
  const stepMillis = stepDays * 86400000;
  const sortedPrices = [...prices].sort((a, b) => a[0] - b[0]);
  if (sortedPrices.length === 0) return [];
  const firstTimestamp = sortedPrices[0][0];
  const lastTimestamp = sortedPrices[sortedPrices.length - 1][0];

  for (let windowStartTs = firstTimestamp; windowStartTs <= lastTimestamp - windowMillis; windowStartTs += stepMillis) {
    const windowEndTs = windowStartTs + windowMillis;
    const windowPrices = sortedPrices.filter(([ts, price]) => ts >= windowStartTs && ts < windowEndTs && price > 0);
    if (windowPrices.length < 1) continue;
    const daily = Array(7).fill().map(() => ({ total: 0, count: 0 }));
    windowPrices.forEach(([ts, price]) => {
      const dow = (new Date(ts).getUTCDay() + 6) % 7;
      daily[dow].total += price;
      daily[dow].count++;
    });
    const dailyAverages = daily.map((d, i) => ({
      dayIndex: i,
      avg: d.count > 0 ? d.total / d.count : 0,
    })).filter((d) => d.avg > 0);
    if (dailyAverages.length < 1) continue;
    dailyAverages.sort((a, b) => a.avg - b.avg);
    trendPoints.push({
      date: new Date(windowStartTs).toISOString().slice(0, 10),
      lowestDay: dailyAverages[0].dayIndex,
      highestDay: dailyAverages[dailyAverages.length - 1].dayIndex,
    });
  }
  return trendPoints;
}

const calculateLoanBacktestData = (loanRawPrices, loanStartDate, loanBTCAmount, loanLTV, loanAPR, loanTermMonths) => {
  if (!loanRawPrices || loanRawPrices.length === 0 || !loanStartDate) return { loanData: [], initialLoanPrincipalInCurrency: 0 };

  const loanStartTimestamp = new Date(loanStartDate).setUTCHours(0, 0, 0, 0);
  const loanEndTimestamp = new Date(loanStartDate);
  loanEndTimestamp.setUTCMonth(loanEndTimestamp.getUTCMonth() + loanTermMonths);
  loanEndTimestamp.setUTCHours(23, 59, 59, 999);

  let initialBTCPriceEntry = loanRawPrices.find(([ts]) => ts >= loanStartTimestamp);
  if (!initialBTCPriceEntry) {
    initialBTCPriceEntry = loanRawPrices.reduce(
      (prev, curr) => (Math.abs(curr[0] - loanStartTimestamp) < Math.abs(prev[0] - loanStartTimestamp) ? curr : prev),
      [0, 0]
    );
    if (initialBTCPriceEntry[0] === 0 && initialBTCPriceEntry[1] === 0) {
      return { loanData: [], initialLoanPrincipalInCurrency: 0 };
    }
  }

  const initialBTCPriceInCurrency = initialBTCPriceEntry[1];
  const initialLoanPrincipalInCurrency = initialBTCPriceInCurrency * loanBTCAmount * loanLTV;
  const dailyInterestRate = loanAPR / 365;

  const loanData = [];
  loanRawPrices.forEach(([currentTimestamp, currentBTCPriceInCurrency]) => {
    if (currentTimestamp >= loanStartTimestamp && currentTimestamp <= loanEndTimestamp.getTime()) {
      const daysSinceLoanStart = (currentTimestamp - loanStartTimestamp) / (1000 * 60 * 60 * 24);
      const currentAssetValue = currentBTCPriceInCurrency * loanBTCAmount;
      const currentLoanDebtValue = initialLoanPrincipalInCurrency * (1 + dailyInterestRate * daysSinceLoanStart);
      loanData.push({
        date: new Date(currentTimestamp).toISOString().slice(0, 10),
        monthOffset: daysSinceLoanStart / (365 / 12),
        daysSinceLoanStart,
        assetValue: currentAssetValue,
        loanDebtValue: currentLoanDebtValue,
      });
    }
  });

  return { loanData, initialLoanPrincipalInCurrency };
};

const calculateStackingData = (priceData, startDateStr, amount, frequency) => {
  if (!priceData || priceData.length === 0 || !startDateStr || !amount) {
    return { data: [], summary: null };
  }

  const startDate = new Date(startDateStr + 'T00:00:00Z');
  const priceMap = new Map(priceData.map(([ts, price]) => {
    const dateKey = new Date(ts).toISOString().split('T')[0];
    return [dateKey, price];
  }));

  let totalInvested = 0;
  let totalBTC = 0;
  const results = [];
  let nextInvestmentDate = new Date(startDate);
  let currentDate = new Date(startDate);
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);

  while (currentDate <= today) {
    const dateKey = currentDate.toISOString().split('T')[0];
    const price = priceMap.get(dateKey);

    if (price > 0 && currentDate >= nextInvestmentDate) {
      totalInvested += amount;
      totalBTC += amount / price;
      switch (frequency) {
        case 'daily': nextInvestmentDate.setUTCDate(nextInvestmentDate.getUTCDate() + 1); break;
        case 'weekly': nextInvestmentDate.setUTCDate(nextInvestmentDate.getUTCDate() + 7); break;
        case 'bi-weekly': nextInvestmentDate.setUTCDate(nextInvestmentDate.getUTCDate() + 14); break;
        case 'monthly': nextInvestmentDate.setUTCMonth(nextInvestmentDate.getUTCMonth() + 1); break;
        default: break;
      }
    }

    if (price > 0) {
      results.push({
        date: dateKey,
        totalInvested,
        totalBTC,
        stackValueUSD: totalBTC * price,
      });
    }
    currentDate.setUTCDate(currentDate.getUTCDate() + 1);
  }

  if (results.length === 0) return { data: [], summary: null };

  const finalDataPoint = results[results.length - 1];
  const summary = {
    totalInvested: finalDataPoint.totalInvested,
    totalBTC: finalDataPoint.totalBTC,
    finalValue: finalDataPoint.stackValueUSD,
    gainLoss: finalDataPoint.stackValueUSD - finalDataPoint.totalInvested,
    gainLossPercent: finalDataPoint.totalInvested > 0
      ? ((finalDataPoint.stackValueUSD - finalDataPoint.totalInvested) / finalDataPoint.totalInvested) * 100
      : 0,
  };
  return { data: results, summary };
};

// ---------------------------------------------------------------------------
// FORMATTERS
// ---------------------------------------------------------------------------
const formatBtcOrSats = (btcValue, unit) => {
  if (unit === 'btc') return `${btcValue.toFixed(8)} BTC`;
  const sats = btcValue * SATOSHIS_PER_BTC;
  if (sats >= 1_000_000_000) return `${(sats / 1_000_000_000).toFixed(2)}B sats`;
  if (sats >= 1_000_000) return `${(sats / 1_000_000).toFixed(2)}M sats`;
  if (sats >= 1_000) return `${(sats / 1_000).toFixed(2)}K sats`;
  return `${sats.toLocaleString('en-US', { maximumFractionDigits: 0 })} sats`;
};

const getQuarterlyTicks = (startDateStr, endDateStr) => {
  const ticks = [];
  let currentDate = new Date(startDateStr);
  const endDate = new Date(endDateStr);
  while (currentDate <= endDate) {
    const month = currentDate.getUTCMonth();
    if (month === 0 || month === 3 || month === 6 || month === 9) {
      ticks.push(new Date(currentDate).toISOString().split('T')[0]);
    }
    currentDate.setUTCMonth(currentDate.getUTCMonth() + 1);
  }
  return ticks;
};

const formatQuarterTick = (tick) => {
  const date = new Date(tick);
  const quarter = Math.floor(date.getUTCMonth() / 3) + 1;
  const year = date.getUTCFullYear().toString().slice(-2);
  return `Q${quarter} '${year}`;
};

// ---------------------------------------------------------------------------
// REUSABLE UI PRIMITIVES (hoisted out of App to prevent remounting)
// ---------------------------------------------------------------------------
const Panel = ({ title, children, accent = false, className = '' }) => (
  <section
    className={`relative border ${className}`}
    style={{
      background: THEME.bgPanel,
      borderColor: accent ? THEME.orange : THEME.border,
    }}
  >
    {title && (
      <header
        className="px-4 py-2 border-b flex items-center justify-between text-xs uppercase tracking-widest"
        style={{
          borderColor: THEME.border,
          color: THEME.textDim,
          background: THEME.bgPanelRaised,
        }}
      >
        <span className="flex items-center gap-2">
          <span style={{ color: THEME.orange }}>❯</span>
          {title}
        </span>
      </header>
    )}
    <div className="p-4">{children}</div>
  </section>
);

const Selector = ({ label, value, onChange, options, icon: Icon }) => (
  <div>
    <label className="flex items-center text-[10px] font-medium uppercase tracking-widest mb-1.5" style={{ color: THEME.textDim }}>
      {Icon && <Icon className="w-3 h-3 mr-1.5" />}
      {label}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 text-sm font-mono uppercase tracking-wide border focus:outline-none focus:ring-1 transition-colors"
      style={{
        background: THEME.bg,
        borderColor: THEME.border,
        color: THEME.text,
      }}
      onFocus={(e) => (e.currentTarget.style.borderColor = THEME.orange)}
      onBlur={(e) => (e.currentTarget.style.borderColor = THEME.border)}
    >
      {Object.entries(options).map(([k, v]) => (
        <option key={k} value={k} style={{ background: THEME.bg }}>
          {v.label || v.name}
        </option>
      ))}
    </select>
  </div>
);

const TextInput = ({ label, icon: Icon, type = 'text', ...props }) => (
  <div>
    <label className="flex items-center text-[10px] font-medium uppercase tracking-widest mb-1.5" style={{ color: THEME.textDim }}>
      {Icon && <Icon className="w-3 h-3 mr-1.5" />}
      {label}
    </label>
    <input
      type={type}
      className="w-full px-3 py-2 text-sm font-mono border focus:outline-none transition-colors"
      style={{
        background: THEME.bg,
        borderColor: THEME.border,
        color: THEME.text,
      }}
      onFocus={(e) => (e.currentTarget.style.borderColor = THEME.orange)}
      onBlur={(e) => (e.currentTarget.style.borderColor = THEME.border)}
      {...props}
    />
  </div>
);

const Stat = ({ label, value, accent }) => (
  <div className="border px-3 py-2" style={{ borderColor: THEME.border, background: THEME.bg }}>
    <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: THEME.textDim }}>
      {label}
    </div>
    <div className="text-base font-mono font-bold" style={{ color: accent || THEME.text }}>
      {value}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// MAIN APP
// ---------------------------------------------------------------------------
export default function App() {
  const [apiSource, setApiSource] = useState('cryptocompare');
  const [timeRange, setTimeRange] = useState('3M');
  const [currency, setCurrency] = useState('usd');
  const [rawPrices, setRawPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [trendWindowRange, setTrendWindowRange] = useState('1M');

  const [customStartDate, setCustomStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d.toISOString().split('T')[0];
  });
  const [customEndDate, setCustomEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const [unitDisplay, setUnitDisplay] = useState('btc');

  // Loan backtesting
  const [loanStartDate, setLoanStartDate] = useState(null);
  const loanBTCAmount = 1;
  const [loanLTV, setLoanLTV] = useState(0.5);
  const [loanAPRInput, setLoanAPRInput] = useState(12);
  const loanTermMonths = 12;

  const [loanRawPrices, setLoanRawPrices] = useState([]);
  const [loanLoading, setLoanLoading] = useState(true);
  const [loanError, setLoanError] = useState(null);
  const [loanWarning, setLoanWarning] = useState(null);

  const [showAdvancedCharts, setShowAdvancedCharts] = useState(false);
  const [costToCover, setCostToCover] = useState(0);
  const [costToCoverBTC, setCostToCoverBTC] = useState(0);
  const [netValue, setNetValue] = useState(0);

  const [fullPriceHistory, setFullPriceHistory] = useState([]);
  const [fullHistoryLoading, setFullHistoryLoading] = useState(true);
  const [optimalDates, setOptimalDates] = useState({ best: null, worst: null, base: null });

  // Stacking calculator — default to 5 years ago
  const [stackingStartDate, setStackingStartDate] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 5);
    return d.toISOString().split('T')[0];
  });
  const [stackingAmount, setStackingAmount] = useState(100);
  const [stackingFrequency, setStackingFrequency] = useState('weekly');
  const [stackingData, setStackingData] = useState([]);
  const [stackingSummary, setStackingSummary] = useState(null);
  const [stackingLoading, setStackingLoading] = useState(true);
  const [stackingError, setStackingError] = useState(null);

  const loanAPR = loanAPRInput / 100;

  // ---- Effects ----
  useEffect(() => {
    setWarning(null);
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        let response;
        if (timeRange === 'CUSTOM') {
          if (!customStartDate || !customEndDate) {
            setRawPrices([]);
            setLoading(false);
            return;
          }
          const startTs = new Date(customStartDate).getTime();
          const endTs = new Date(customEndDate).setHours(23, 59, 59, 999);
          if (startTs >= endTs) throw new Error('Start date must be before end date.');
          response = await fetchAssetDataWithRange(apiSource, currency, null, startTs, endTs, setWarning);
        } else {
          const days = TIME_RANGES[timeRange].days;
          response = await fetchAssetDataWithRange(apiSource, currency, days, null, null, setWarning);
        }
        const prices = response.prices;
        if (!prices || !prices.length) throw new Error('No price data returned from API for the selected range.');
        setRawPrices(prices);
      } catch (err) {
        setError(err.message);
        setRawPrices([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [apiSource, currency, timeRange, customStartDate, customEndDate]);

  useEffect(() => {
    const calculate = async () => {
      if (!stackingStartDate) {
        setStackingData([]);
        setStackingSummary(null);
        return;
      }
      setStackingLoading(true);
      setStackingError(null);
      try {
        const startTs = new Date(stackingStartDate).getTime();
        const endTs = new Date().getTime();
        if (startTs >= endTs) throw new Error('Stacking start date must be in the past.');
        const response = await fetchAssetDataWithRange(apiSource, currency, null, startTs, endTs, () => {});
        const prices = response.prices;
        if (!prices || prices.length === 0) throw new Error('No price data available for the selected stacking period.');
        const { data, summary } = calculateStackingData(prices, stackingStartDate, stackingAmount, stackingFrequency);
        setStackingData(data);
        setStackingSummary(summary);
      } catch (err) {
        setStackingError(err.message);
        setStackingData([]);
        setStackingSummary(null);
      } finally {
        setStackingLoading(false);
      }
    };
    calculate();
  }, [stackingStartDate, stackingAmount, stackingFrequency, apiSource, currency]);

  useEffect(() => {
    if (!loanStartDate) return;
    setLoanWarning(null);
    const fetchLoanData = async () => {
      setLoanLoading(true);
      setLoanError(null);
      try {
        const startDate = new Date(loanStartDate);
        const loanEndDate = new Date(startDate);
        loanEndDate.setUTCMonth(loanEndDate.getUTCMonth() + loanTermMonths);
        const response = await fetchAssetDataWithRange(apiSource, currency, null, startDate.getTime(), loanEndDate.getTime(), setLoanWarning);
        const prices = response.prices;
        if (!prices || !prices.length) throw new Error('No price data returned for the selected loan period.');
        setLoanRawPrices(prices);
      } catch (err) {
        setLoanError(err.message);
        setLoanRawPrices([]);
      } finally {
        setLoanLoading(false);
      }
    };
    fetchLoanData();
  }, [apiSource, currency, loanStartDate, loanTermMonths]);

  useEffect(() => {
    const fetchFullHistory = async () => {
      setFullHistoryLoading(true);
      try {
        const response = await fetchAssetDataWithRange('cryptocompare', 'usd', 1460, null, null, () => {});
        const prices = response.prices;
        if (!prices || !prices.length) throw new Error('No full price history returned.');
        setFullPriceHistory(prices);
      } catch (err) {
        console.error('Failed to fetch full price history for loan presets:', err);
      } finally {
        setFullHistoryLoading(false);
      }
    };
    fetchFullHistory();
  }, []);

  useEffect(() => {
    if (fullHistoryLoading || fullPriceHistory.length === 0) return;

    const calculateOptimalDates = (prices, btcAmount, ltv, apr, termMonths) => {
      const outcomes = [];
      const loanTermMillis = termMonths * 30.44 * 24 * 60 * 60 * 1000;
      for (let i = 0; i < prices.length; i++) {
        const potentialStartDate = new Date(prices[i][0]);
        const loanStartTimestamp = potentialStartDate.getTime();
        const loanEndTimestamp = loanStartTimestamp + loanTermMillis;
        if (loanEndTimestamp > prices[prices.length - 1][0]) break;
        const loanPrices = prices.filter(([ts]) => ts >= loanStartTimestamp && ts <= loanEndTimestamp);
        if (loanPrices.length < 2) continue;
        const { loanData } = calculateLoanBacktestData(loanPrices, potentialStartDate.toISOString().split('T')[0], btcAmount, ltv, apr, termMonths);
        if (loanData.length === 0) continue;
        const finalDataPoint = loanData[loanData.length - 1];
        const finalProfit = finalDataPoint.assetValue - finalDataPoint.loanDebtValue;
        let maxCostToCover = 0;
        loanData.forEach((point) => {
          const cost = point.loanDebtValue - point.assetValue;
          if (cost > maxCostToCover) maxCostToCover = cost;
        });
        outcomes.push({
          date: potentialStartDate.toISOString().split('T')[0],
          profit: finalProfit,
          costToCover: maxCostToCover,
        });
      }
      if (outcomes.length === 0) return { best: null, worst: null, base: null };
      const sortedByProfit = [...outcomes].sort((a, b) => b.profit - a.profit);
      const sortedByCost = [...outcomes].sort((a, b) => b.costToCover - a.costToCover);
      return {
        best: sortedByProfit[0].date,
        base: sortedByProfit[Math.floor(sortedByProfit.length / 2)].date,
        worst: sortedByCost[0].date,
      };
    };

    const optimal = calculateOptimalDates(fullPriceHistory, loanBTCAmount, loanLTV, loanAPR, loanTermMonths);
    setOptimalDates(optimal);
  }, [fullHistoryLoading, fullPriceHistory, loanLTV, loanAPR, loanBTCAmount, loanTermMonths]);

  useEffect(() => {
    if (optimalDates.base && !loanStartDate) setLoanStartDate(optimalDates.base);
  }, [optimalDates.base, loanStartDate]);

  // ---- Derived values ----
  const priceUnitLabel = unitDisplay === 'sats' ? 'SATS' : 'BTC';

  const chartData = useMemo(() => {
    if (!rawPrices.length) return [];
    const processed = processPriceData(rawPrices);
    return processed.map((d) => ({
      ...d,
      percentDiff: unitDisplay === 'sats' ? -d.percentDiff : d.percentDiff,
      averagePrice: unitDisplay === 'sats' && d.averagePrice > 0 ? SATOSHIS_PER_BTC / d.averagePrice : d.averagePrice,
    }));
  }, [rawPrices, unitDisplay]);

  const trendData = useMemo(() => {
    if (!rawPrices.length) return [];
    const windowDays = TIME_RANGES[trendWindowRange].days;
    const stepDays = Math.max(7, Math.floor(windowDays / 6));
    return processTrendData(rawPrices, windowDays, stepDays);
  }, [rawPrices, trendWindowRange]);

  // Loan calc derivation — separated principal into its own memo to avoid setState inside memo
  const { loanBacktestResults, initialLoanPrincipalForDisplay } = useMemo(() => {
    if (!loanStartDate) return { loanBacktestResults: [], initialLoanPrincipalForDisplay: 0 };
    const { loanData, initialLoanPrincipalInCurrency } = calculateLoanBacktestData(
      loanRawPrices, loanStartDate, loanBTCAmount, loanLTV, loanAPR, loanTermMonths
    );
    if (!loanData || loanData.length === 0) {
      return { loanBacktestResults: [], initialLoanPrincipalForDisplay: initialLoanPrincipalInCurrency };
    }
    const totalAmountToRepay = initialLoanPrincipalInCurrency + (initialLoanPrincipalInCurrency * loanAPR);
    const monthlyPayment = totalAmountToRepay / loanTermMonths;
    const updatedLoanData = loanData.map((point) => {
      const monthsPassed = Math.floor(point.daysSinceLoanStart / (365 / 12));
      const cumulativePayments = monthlyPayment * monthsPassed;
      const remainingLoanBalance = Math.max(0, point.loanDebtValue - cumulativePayments);
      return { ...point, remainingLoanBalance };
    });
    return { loanBacktestResults: updatedLoanData, initialLoanPrincipalForDisplay: initialLoanPrincipalInCurrency };
  }, [loanRawPrices, loanStartDate, loanBTCAmount, loanLTV, loanAPR, loanTermMonths]);

  useEffect(() => {
    if (loanBacktestResults && loanBacktestResults.length > 0) {
      const latest = loanBacktestResults[loanBacktestResults.length - 1];
      const diff = latest.assetValue - latest.loanDebtValue;
      if (diff > 0) {
        setNetValue(diff); setCostToCover(0); setCostToCoverBTC(0);
      } else {
        setNetValue(0);
        const cost = Math.abs(diff);
        setCostToCover(cost);
        if (loanRawPrices.length > 0) {
          const latestBTCPrice = loanRawPrices[loanRawPrices.length - 1][1];
          setCostToCoverBTC(latestBTCPrice > 0 ? cost / latestBTCPrice : 0);
        } else {
          setCostToCoverBTC(0);
        }
      }
    } else {
      setNetValue(0); setCostToCover(0); setCostToCoverBTC(0);
    }
  }, [loanBacktestResults, loanRawPrices]);

  const estimatedMonthlyPayment = useMemo(() => {
    if (initialLoanPrincipalForDisplay === 0 || loanTermMonths === 0) return 0;
    const totalAmountToRepay = initialLoanPrincipalForDisplay + (initialLoanPrincipalForDisplay * loanAPR);
    return totalAmountToRepay / loanTermMonths;
  }, [initialLoanPrincipalForDisplay, loanAPR, loanTermMonths]);

  const priceChangePercent = useMemo(() => {
    if (rawPrices.length < 2) return 0;
    const firstPrice = rawPrices[0][1];
    const lastPrice = rawPrices[rawPrices.length - 1][1];
    if (firstPrice === 0) return 0;
    if (unitDisplay === 'sats') {
      if (lastPrice === 0) return 0;
      const first = SATOSHIS_PER_BTC / firstPrice;
      const last = SATOSHIS_PER_BTC / lastPrice;
      return ((last - first) / first) * 100;
    }
    return ((lastPrice - firstPrice) / firstPrice) * 100;
  }, [rawPrices, unitDisplay]);

  const { bestDay, worstDay, bestPerf, worstPerf, bestTime, worstTime } = useMemo(() => {
    if (!chartData || chartData.length === 0) return {};
    const valid = chartData.filter((d) => d.percentDiff !== undefined);
    if (!valid.length) return {};
    const sorted = [...valid].sort((a, b) => a.percentDiff - b.percentDiff);
    const lowest = sorted[0];
    const highest = sorted[sorted.length - 1];
    if (unitDisplay === 'sats') {
      return {
        bestDay: highest?.name || null,
        worstDay: lowest?.name || null,
        bestPerf: highest?.percentDiff || 0,
        worstPerf: lowest?.percentDiff || 0,
        bestTime: highest?.maxTime || null,
        worstTime: lowest?.minTime || null,
      };
    }
    return {
      bestDay: lowest?.name || null,
      worstDay: highest?.name || null,
      bestPerf: lowest?.percentDiff || 0,
      worstPerf: highest?.percentDiff || 0,
      bestTime: lowest?.minTime || null,
      worstTime: highest?.maxTime || null,
    };
  }, [chartData, unitDisplay]);

  const formatPriceForDisplay = useCallback((value) => {
    if (unitDisplay === 'sats') return `${value.toLocaleString('en-US', { maximumFractionDigits: 0 })} sats`;
    return `${CURRENCIES[currency].symbol}${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, [unitDisplay, currency]);

  // Common recharts props
  const axisProps = {
    stroke: THEME.textDim,
    tick: { fill: THEME.textDim, fontSize: 11, fontFamily: 'monospace' },
    tickLine: { stroke: THEME.border },
    axisLine: { stroke: THEME.border },
  };
  const gridProps = { stroke: THEME.grid, strokeDasharray: '2 4' };
  const tooltipStyle = {
    contentStyle: {
      background: THEME.bg,
      border: `1px solid ${THEME.orange}`,
      fontFamily: 'monospace',
      fontSize: 12,
      color: THEME.text,
    },
    itemStyle: { color: THEME.text },
    labelStyle: { color: THEME.orange, fontWeight: 'bold' },
    cursor: { fill: 'rgba(247, 147, 26, 0.08)' },
  };

  // ---- Subcomponents (local; read theme from enclosing scope) ----
  const BarChartComponent = ({ data }) => (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={data} margin={{ top: 5, right: 15, left: 0, bottom: 5 }}>
        <CartesianGrid {...gridProps} />
        <XAxis dataKey="name" {...axisProps} />
        <YAxis tickFormatter={(v) => `${v.toFixed(1)}%`} {...axisProps} />
        <Tooltip
          formatter={(value, name, props) => [
            `${value.toFixed(2)}% vs weekly avg`,
            `avg price: ${formatPriceForDisplay(props.payload.averagePrice)}`,
          ]}
          labelFormatter={(l) => l}
          {...tooltipStyle}
        />
        <Bar dataKey="percentDiff" name="% vs avg">
          {data.map((entry, i) => {
            let fillColor = THEME.borderBright;
            if (entry.name === bestDay) fillColor = THEME.green;
            else if (entry.name === worstDay) fillColor = THEME.red;
            return <Cell key={`cell-${i}`} fill={fillColor} />;
          })}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );

  const LineChartComponent = ({ data }) => {
    if (!data || data.length < 2) {
      return (
        <div className="text-center p-8 text-sm" style={{ color: THEME.textFaint }}>
          Not enough data to display trend analysis for this range.
        </div>
      );
    }
    const quarterlyTicks = getQuarterlyTicks(data[0].date, data[data.length - 1].date);
    return (
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={data} margin={{ top: 5, right: 15, left: 0, bottom: 5 }}>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="date" ticks={quarterlyTicks} tickFormatter={formatQuarterTick} {...axisProps} />
          <YAxis
            domain={[0, 6]}
            ticks={[0, 1, 2, 3, 4, 5, 6]}
            tickFormatter={(tick) => Y_AXIS_DAY_LABELS_ORDERED[tick] || ''}
            width={40}
            {...axisProps}
          />
          <Tooltip
            labelFormatter={(d) => `window: ${new Date(d).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: '2-digit' })}`}
            formatter={(value, name) => [DAY_NAMES[value], name]}
            {...tooltipStyle}
          />
          <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'monospace', color: THEME.textDim }} />
          <Line type="monotone" name="best day" dataKey="lowestDay" stroke={THEME.green} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
          <Line type="monotone" name="worst day" dataKey="highestDay" stroke={THEME.red} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const ActualPriceChart = ({ data }) => {
    const formatted = data.map(([timestamp, price]) => ({
      date: new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      price: unitDisplay === 'sats' && price > 0 ? SATOSHIS_PER_BTC / price : price,
    }));
    return (
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={formatted} margin={{ top: 5, right: 15, left: 0, bottom: 5 }}>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="date" hide={true} />
          <YAxis type="number" domain={['dataMin', 'dataMax']} scale="log" allowDataOverflow={true} hide={true} />
          <Tooltip
            content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                return (
                  <div
                    className="px-3 py-2 font-mono text-xs"
                    style={{ background: THEME.bg, border: `1px solid ${THEME.orange}`, color: THEME.text }}
                  >
                    <div style={{ color: THEME.orange, fontWeight: 'bold' }}>{formatPriceForDisplay(payload[0].value)}</div>
                    <div style={{ color: THEME.textDim }}>{label}</div>
                  </div>
                );
              }
              return null;
            }}
            cursor={{ stroke: THEME.orange, strokeWidth: 1, strokeDasharray: '3 3' }}
          />
          <Line type="monotone" dataKey="price" stroke={THEME.orange} dot={false} strokeWidth={1.5} />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const LoanBacktestChart = ({ data, currencySymbol, loanPrincipalBTC, termMonths, isLoading, err }) => {
    if (isLoading) {
      return (
        <div className="text-center p-8">
          <div className="text-sm font-mono animate-pulse" style={{ color: THEME.orange }}>[LOADING LOAN DATA...]</div>
        </div>
      );
    }
    if (err) return <div className="p-3 text-sm font-mono" style={{ background: THEME.bg, border: `1px solid ${THEME.red}`, color: THEME.red }}>ERR: {err}</div>;
    if (!data || data.length < 2) {
      return <div className="text-center p-8 text-sm" style={{ color: THEME.textFaint }}>Not enough data for loan backtest. Try a different start date.</div>;
    }
    return (
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={data} margin={{ top: 5, right: 15, left: 0, bottom: 5 }}>
          <CartesianGrid {...gridProps} />
          <XAxis
            dataKey="monthOffset"
            type="number"
            domain={[0, termMonths]}
            ticks={[0, 3, 6, 9, 12]}
            tickFormatter={(v) => `${Math.round(v)}M`}
            {...axisProps}
          />
          <YAxis
            tickFormatter={(value) => `${currencySymbol}${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
            {...axisProps}
          />
          <Tooltip
            labelFormatter={(v) => `Month: ${v.toFixed(1)}`}
            formatter={(v, n) => [`${currencySymbol}${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, n]}
            {...tooltipStyle}
          />
          <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'monospace' }} />
          <Line type="monotone" name={`Collateral (${loanPrincipalBTC} BTC)`} dataKey="assetValue" stroke={THEME.green} strokeWidth={2} dot={false} />
          <Line type="monotone" name="Debt (principal + interest)" dataKey="loanDebtValue" stroke={THEME.red} strokeWidth={2} dot={false} />
          <Line type="stepAfter" name="Remaining Balance" dataKey="remainingLoanBalance" stroke={THEME.orange} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const CombinedStackingChart = ({ data, currencySymbol }) => {
    const formatCurrency = (value) => `${currencySymbol}${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    const formatRightYAxis = (value) => {
      if (unitDisplay === 'btc') return `${value.toFixed(2)}`;
      if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
      if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
      if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
      return value;
    };
    const processed = data.map((d) => ({
      ...d,
      displayAmount: unitDisplay === 'sats' ? d.totalBTC * SATOSHIS_PER_BTC : d.totalBTC,
    }));
    const quarterlyTicks = getQuarterlyTicks(data[0].date, data[data.length - 1].date);

    return (
      <ResponsiveContainer width="100%" height={360}>
        <ComposedChart data={processed} margin={{ top: 5, right: 15, left: 0, bottom: 5 }}>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="date" type="category" ticks={quarterlyTicks} tickFormatter={formatQuarterTick} {...axisProps} />
          <YAxis yAxisId="left" orientation="left" stroke={THEME.indigo} tickFormatter={formatCurrency} tick={{ fill: THEME.textDim, fontSize: 11, fontFamily: 'monospace' }} />
          <YAxis yAxisId="right" orientation="right" stroke={THEME.orange} tickFormatter={formatRightYAxis} tick={{ fill: THEME.textDim, fontSize: 11, fontFamily: 'monospace' }} />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload || !payload.length) return null;
              return (
                <div className="px-3 py-2 font-mono text-xs" style={{ background: THEME.bg, border: `1px solid ${THEME.orange}`, color: THEME.text }}>
                  <div style={{ color: THEME.orange, fontWeight: 'bold' }}>
                    {new Date(label).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </div>
                  {payload.map((p) => {
                    let v;
                    if (p.dataKey === 'displayAmount') {
                      v = unitDisplay === 'sats' ? `${Math.floor(p.value).toLocaleString()} sats` : `${p.value.toFixed(8)} BTC`;
                    } else {
                      v = `${currencySymbol}${p.value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
                    }
                    return <div key={p.name} style={{ color: p.color }}>{p.name}: {v}</div>;
                  })}
                </div>
              );
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'monospace' }} />
          <Area yAxisId="left" type="monotone" dataKey="totalInvested" name="Cost Basis" stroke={THEME.indigo} fill={THEME.indigo} fillOpacity={0.25} />
          <Area yAxisId="left" type="monotone" dataKey="stackValueUSD" name="Stack Value" stroke={THEME.green} fill={THEME.green} fillOpacity={0.25} />
          <Line yAxisId="right" type="monotone" dataKey="displayAmount" name={`Stack (${unitDisplay === 'sats' ? 'sats' : 'BTC'})`} stroke={THEME.orange} dot={false} strokeWidth={1.5} />
        </ComposedChart>
      </ResponsiveContainer>
    );
  };

  // ---- Render ----
  const currencySymbol = CURRENCIES[currency].symbol;
  const currencyName = CURRENCIES[currency].name;

  return (
    <div
      className="min-h-screen w-full"
      style={{
        background: THEME.bg,
        color: THEME.text,
        fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", Menlo, Consolas, monospace',
        backgroundImage: `radial-gradient(circle at 50% -20%, rgba(247, 147, 26, 0.06), transparent 60%)`,
      }}
    >
      {/* Subtle scanline overlay for that terminal feel */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0"
        style={{
          background: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.015) 0px, rgba(255,255,255,0.015) 1px, transparent 1px, transparent 3px)',
          zIndex: 0,
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 py-6" style={{ zIndex: 1 }}>
        {/* HEADER */}
        <header
          className="border px-4 py-3 mb-6 flex items-center justify-between flex-wrap gap-3"
          style={{ background: THEME.bgPanel, borderColor: THEME.border }}
        >
          <div className="flex items-center gap-3">
            <Terminal className="w-5 h-5" style={{ color: THEME.orange }} />
            <div>
              <h1 className="text-lg sm:text-xl font-bold tracking-tight">
                <span style={{ color: THEME.orange }}>stacking</span>
                <span style={{ color: THEME.textFaint }}>.</span>
                <span style={{ color: THEME.text }}>day</span>
              </h1>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: THEME.textFaint }}>
                btc / weekday analysis / v0
              </div>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: THEME.textDim }}>
            <span style={{ color: THEME.green }}>●</span> live · {new Date().toISOString().split('T')[0]}
          </div>
        </header>

        {/* PITCH */}
        <p className="mb-6 text-sm leading-relaxed" style={{ color: THEME.textDim }}>
          <span style={{ color: THEME.orange }}>{'> '}</span>
          You likely get paid on a Friday, but is that the best day of the week to stack?{' '}
          Decide for yourself. <span style={{ color: THEME.text }}>Stay humble, stack sats.</span>
        </p>

        {/* CONTROLS */}
        <Panel title="query.config" className="mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Selector label="API Source" value={apiSource} onChange={setApiSource} options={APIS} icon={Database} />
            <Selector label="Time Range" value={timeRange} onChange={setTimeRange} options={TIME_RANGES} icon={Calendar} />
            <Selector label="Currency" value={currency} onChange={setCurrency} options={CURRENCIES} icon={CURRENCIES[currency].icon} />
            <Selector label="Unit" value={unitDisplay} onChange={setUnitDisplay} options={{ btc: { label: 'BTC' }, sats: { label: 'SATS' } }} icon={Bitcoin} />
          </div>
          {timeRange === 'CUSTOM' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4" style={{ borderTop: `1px dashed ${THEME.border}` }}>
              <TextInput
                label="Start Date"
                icon={Calendar}
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
              <TextInput
                label="End Date"
                icon={Calendar}
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
          )}
        </Panel>

        {warning && (
          <div className="mb-4 px-4 py-2 text-xs font-mono" style={{ background: THEME.bg, border: `1px solid ${THEME.yellow}`, color: THEME.yellow }}>
            <span style={{ color: THEME.yellow }}>[warn]</span> {warning}
          </div>
        )}

        {/* WEEKLY ANALYSIS */}
        <Panel title={`weekday.analysis  —  ${timeRange === 'CUSTOM' ? 'custom' : TIME_RANGES[timeRange].label.toLowerCase()}`} accent className="mb-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="text-sm font-mono animate-pulse" style={{ color: THEME.orange }}>[FETCHING MARKET DATA...]</div>
            </div>
          ) : error ? (
            <div className="p-3 text-sm font-mono" style={{ background: THEME.bg, border: `1px solid ${THEME.red}`, color: THEME.red }}>
              ERR: {error}
            </div>
          ) : chartData.length === 0 ? (
            <div className="text-center py-8 text-sm" style={{ color: THEME.textFaint }}>
              Not enough data. Select a longer period (≥ 7 days).
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                <div className="border p-4" style={{ borderColor: THEME.green, background: THEME.bg }}>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest mb-1" style={{ color: THEME.green }}>
                    <span>[+]</span> stack sats
                  </div>
                  <div className="text-2xl font-bold mb-1">{bestDay}</div>
                  <div className="text-sm" style={{ color: THEME.textDim }}>
                    {bestPerf > 0 ? '+' : ''}{bestPerf.toFixed(2)}%{' '}
                    <span style={{ color: THEME.textFaint }}>vs weekly avg</span>
                    {bestTime && <span style={{ color: THEME.textFaint }}> · {fmtTime(bestTime)}</span>}
                  </div>
                </div>
                <div className="border p-4" style={{ borderColor: THEME.red, background: THEME.bg }}>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest mb-1" style={{ color: THEME.red }}>
                    <span>[-]</span> stay humble
                  </div>
                  <div className="text-2xl font-bold mb-1">{worstDay}</div>
                  <div className="text-sm" style={{ color: THEME.textDim }}>
                    {worstPerf > 0 ? '+' : ''}{worstPerf.toFixed(2)}%{' '}
                    <span style={{ color: THEME.textFaint }}>vs weekly avg</span>
                    {worstTime && <span style={{ color: THEME.textFaint }}> · {fmtTime(worstTime)}</span>}
                  </div>
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: THEME.textDim }}>
                day of week vs. weekly average
              </div>
              <BarChartComponent data={chartData} />
            </>
          )}
        </Panel>

        {/* STACKING CALCULATOR */}
        <Panel title="stacking.calculator" className="mb-6">
          <div className="flex items-start gap-2 mb-4">
            <TrendingUp className="w-4 h-4 mt-0.5" style={{ color: THEME.orange }} />
            <div className="text-xs" style={{ color: THEME.textDim }}>
              Simulate a DCA strategy. See how a recurring buy would have played out.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <TextInput
              label="Start Date"
              icon={Calendar}
              type="date"
              value={stackingStartDate}
              onChange={(e) => setStackingStartDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
            />
            <TextInput
              label={`Amount (${currencyName})`}
              icon={DollarSign}
              type="number"
              value={stackingAmount}
              onChange={(e) => setStackingAmount(parseFloat(e.target.value) || 0)}
            />
            <Selector
              label="Frequency"
              icon={Repeat}
              value={stackingFrequency}
              onChange={setStackingFrequency}
              options={{
                daily: { label: 'DAILY' },
                weekly: { label: 'WEEKLY' },
                'bi-weekly': { label: 'BI-WEEKLY' },
                monthly: { label: 'MONTHLY' },
              }}
            />
          </div>

          {stackingLoading ? (
            <div className="text-center py-10">
              <div className="text-sm font-mono animate-pulse" style={{ color: THEME.orange }}>[CALCULATING STACK...]</div>
            </div>
          ) : stackingError ? (
            <div className="p-3 text-sm font-mono" style={{ background: THEME.bg, border: `1px solid ${THEME.red}`, color: THEME.red }}>
              ERR: {stackingError}
            </div>
          ) : stackingSummary && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <Stat
                  label="total invested"
                  value={`${currencySymbol}${stackingSummary.totalInvested.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
                />
                <Stat
                  label="stack value"
                  value={`${currencySymbol}${stackingSummary.finalValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
                />
                <Stat
                  label="total stack"
                  value={formatBtcOrSats(stackingSummary.totalBTC, unitDisplay)}
                  accent={THEME.orange}
                />
                <Stat
                  label="gain / loss"
                  value={`${stackingSummary.gainLossPercent >= 0 ? '+' : ''}${stackingSummary.gainLossPercent.toFixed(2)}%`}
                  accent={stackingSummary.gainLossPercent >= 0 ? THEME.green : THEME.red}
                />
              </div>
              <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: THEME.textDim }}>
                stack performance
              </div>
              <CombinedStackingChart data={stackingData} currencySymbol={currencySymbol} />
            </>
          )}
        </Panel>

        {/* ACTUAL PRICE */}
        {!loading && !error && rawPrices.length > 0 && (
          <Panel title={`price.history  —  ${priceUnitLabel}/${currencyName}`} className="mb-6">
            <div className="flex items-start justify-between mb-3">
              <div className="text-xs" style={{ color: THEME.textDim }}>
                Actual BTC price over selected range · log scale
              </div>
              {rawPrices.length > 1 && (
                <div
                  className="text-sm font-bold font-mono"
                  style={{ color: priceChangePercent >= 0 ? THEME.green : THEME.red }}
                >
                  {priceChangePercent >= 0 ? '+' : ''}{priceChangePercent.toFixed(2)}%
                </div>
              )}
            </div>
            <ActualPriceChart data={rawPrices} />
          </Panel>
        )}

        {/* ADVANCED */}
        <button
          onClick={() => setShowAdvancedCharts(!showAdvancedCharts)}
          className="w-full border px-4 py-3 mb-6 flex items-center justify-between text-xs uppercase tracking-widest transition-colors hover:border-orange-500"
          style={{
            background: THEME.bgPanel,
            borderColor: showAdvancedCharts ? THEME.orange : THEME.border,
            color: THEME.text,
          }}
        >
          <span className="flex items-center gap-2">
            <span style={{ color: THEME.orange }}>❯</span>
            advanced / experimental
          </span>
          {showAdvancedCharts ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {showAdvancedCharts && (
          <>
            {/* LOAN BACKTEST */}
            <Panel title="loan.backtest  —  12 months" className="mb-6">
              {loanWarning && (
                <div className="mb-4 px-3 py-2 text-xs font-mono" style={{ background: THEME.bg, border: `1px solid ${THEME.yellow}`, color: THEME.yellow }}>
                  <span>[warn]</span> {loanWarning}
                </div>
              )}
              <div className="text-xs mb-4" style={{ color: THEME.textDim }}>
                Simulate a loan of 1 BTC at custom APR and LTV. Compare collateral value vs. loan debt over time.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <label className="flex items-center text-[10px] font-medium uppercase tracking-widest" style={{ color: THEME.textDim }}>
                      <Calendar className="w-3 h-3 mr-1.5" /> Loan Start Date
                    </label>
                    <div className="flex gap-1">
                      <button
                        onClick={() => optimalDates.best && setLoanStartDate(optimalDates.best)}
                        disabled={fullHistoryLoading || !optimalDates.best}
                        className="px-2 py-0.5 text-[10px] uppercase tracking-wider border transition-colors disabled:opacity-40"
                        style={{ borderColor: THEME.green, color: THEME.green, background: THEME.bg }}
                        title={`Max profit: ${optimalDates.best || '...'}`}
                      >
                        best
                      </button>
                      <button
                        onClick={() => optimalDates.base && setLoanStartDate(optimalDates.base)}
                        disabled={fullHistoryLoading || !optimalDates.base}
                        className="px-2 py-0.5 text-[10px] uppercase tracking-wider border transition-colors disabled:opacity-40"
                        style={{ borderColor: THEME.border, color: THEME.textDim, background: THEME.bg }}
                        title={`Median: ${optimalDates.base || '...'}`}
                      >
                        base
                      </button>
                      <button
                        onClick={() => optimalDates.worst && setLoanStartDate(optimalDates.worst)}
                        disabled={fullHistoryLoading || !optimalDates.worst}
                        className="px-2 py-0.5 text-[10px] uppercase tracking-wider border transition-colors disabled:opacity-40"
                        style={{ borderColor: THEME.red, color: THEME.red, background: THEME.bg }}
                        title={`Worst: ${optimalDates.worst || '...'}`}
                      >
                        worst
                      </button>
                    </div>
                  </div>
                  <input
                    type="date"
                    value={loanStartDate || ''}
                    onChange={(e) => setLoanStartDate(e.target.value)}
                    className="w-full px-3 py-2 text-sm font-mono border focus:outline-none"
                    style={{ background: THEME.bg, borderColor: THEME.border, color: THEME.text }}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <TextInput
                  label="LTV Ratio (%)"
                  type="number"
                  value={loanLTV * 100}
                  onChange={(e) => setLoanLTV(parseFloat(e.target.value) / 100 || 0)}
                  min="0"
                  max="100"
                  step="1"
                />
                <TextInput
                  label="APR (%)"
                  type="number"
                  value={loanAPRInput}
                  onChange={(e) => setLoanAPRInput(parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.1"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 text-xs font-mono">
                {estimatedMonthlyPayment > 0 && (
                  <Stat
                    label="est. monthly payment"
                    value={`${currencySymbol}${estimatedMonthlyPayment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    accent={THEME.text}
                  />
                )}
                {netValue > 0 && (
                  <Stat
                    label="net value"
                    value={`${currencySymbol}${netValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    accent={THEME.green}
                  />
                )}
                {costToCover > 0 && (
                  <Stat
                    label="cost to cover (margin call)"
                    value={`${currencySymbol}${costToCover.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · ${costToCoverBTC.toFixed(8)} BTC`}
                    accent={THEME.red}
                  />
                )}
              </div>

              <LoanBacktestChart
                data={loanBacktestResults}
                currencySymbol={currencySymbol}
                loanPrincipalBTC={loanBTCAmount}
                termMonths={loanTermMonths}
                isLoading={loanLoading}
                err={loanError}
              />
            </Panel>

            {/* TREND / PRECESSION */}
            <Panel title="precession  —  best & worst days over time" className="mb-6">
              <div className="text-xs mb-4" style={{ color: THEME.textDim }}>
                Rolling window showing how the best (green) and worst (red) stacking days shift over time.
              </div>
              <div className="max-w-xs mb-4">
                <Selector
                  label="Rolling Window"
                  value={trendWindowRange}
                  onChange={setTrendWindowRange}
                  options={TIME_RANGES}
                  icon={Calendar}
                />
              </div>
              <LineChartComponent data={trendData} />
            </Panel>
          </>
        )}

        {/* FOOTER */}
        <footer className="mt-8 pt-4 text-[10px] uppercase tracking-widest text-center" style={{ color: THEME.textFaint, borderTop: `1px dashed ${THEME.border}` }}>
          <div className="mb-1">not financial advice · verify your own data · run your own node</div>
          <div>
            <span style={{ color: THEME.orange }}>❯</span> stacking.day · open source
          </div>
        </footer>
      </div>
    </div>
  );
}